import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { useFeeSchedulesWithItems } from '../../hooks/useFeeSchedules';
import { calculatePreview, formatCents } from '../../utils/feePreview';
import {
  FEE_RECIPIENT_LABELS,
  CHARGE_EFFECT_LABELS,
  PAYOUT_EFFECT_LABELS,
  CALCULATION_BASIS_LABELS,
  CONTEXT_LABELS,
} from '../../utils/feeScheduleLabels';

export default function FeeScheduleDetailsPage() {
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();
  const { adminUser, isLoading: authLoading } = useAdminAuth();
  const { data: schedules, isLoading: isLoadingSchedules } = useFeeSchedulesWithItems();
  const [previewAmount, setPreviewAmount] = useState(10000); // cents, default $100

  const schedule = schedules?.find(s => s.id === scheduleId);
  const preview = schedule?.items ? calculatePreview(schedule.items, previewAmount) : null;

  if (authLoading || isLoadingSchedules) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1421]" />
      </div>
    );
  }

  if (!adminUser) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-[#A9A3A3]">Unauthorized. Admin access required.</p>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="p-6 max-w-6xl mx-auto font-['Inter']">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/fee-schedules')}
          className="mb-4 text-[#A9A3A3] hover:text-[#EBFFEE] px-0"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Fee Schedules
        </Button>
        <p className="text-[#A9A3A3]">Fee schedule not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto font-['Inter']">
      {/* Back Navigation */}
      <Button
        variant="ghost"
        onClick={() => navigate('/admin/fee-schedules')}
        className="mb-4 text-[#A9A3A3] hover:text-[#EBFFEE] px-0"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Fee Schedules
      </Button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-bold text-[#EBFFEE]">{schedule.name}</h1>
          <span className="px-2 py-0.5 rounded text-xs bg-[#363232] text-[#A9A3A3]">
            {CONTEXT_LABELS[schedule.context] || schedule.context}
          </span>
          {schedule.is_default && (
            <span className="px-2 py-0.5 rounded text-xs bg-[#9B1421] text-white">
              Default
            </span>
          )}
        </div>
        {schedule.description && (
          <p className="text-[#A9A3A3] mt-1">{schedule.description}</p>
        )}
      </div>

      {/* Line Items Table */}
      <Card className="bg-[#221F1F] border-[#504A4A] mb-8">
        <CardHeader>
          <CardTitle className="text-[#EBFFEE] text-base">Fee Schedule Items</CardTitle>
        </CardHeader>
        <CardContent>
          {schedule.items.length === 0 ? (
            <p className="text-[#A9A3A3] text-sm">No line items configured for this schedule.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#504A4A]">
                    <th className="text-left py-3 px-2 text-[#A9A3A3] font-medium">Name</th>
                    <th className="text-left py-3 px-2 text-[#A9A3A3] font-medium">Customer Effect</th>
                    <th className="text-left py-3 px-2 text-[#A9A3A3] font-medium">Basis</th>
                    <th className="text-left py-3 px-2 text-[#A9A3A3] font-medium">Fee</th>
                    <th className="text-left py-3 px-2 text-[#A9A3A3] font-medium">Payout Effect</th>
                    <th className="text-left py-3 px-2 text-[#A9A3A3] font-medium">Recipient</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.items.map((item, idx) => {
                    const feeCalcParts: string[] = [];
                    if (item.fee_percentage) {
                      feeCalcParts.push(`${(item.fee_percentage * 100).toFixed(2)}%`);
                    }
                    if (item.flat_amount_cents) {
                      feeCalcParts.push(formatCents(item.flat_amount_cents));
                    }
                    const feeCalc = feeCalcParts.length > 0 ? feeCalcParts.join(' + ') : '-';

                    const recipientInfo = FEE_RECIPIENT_LABELS[item.fee_recipient] || { label: item.fee_recipient, description: '' };
                    const chargeInfo = CHARGE_EFFECT_LABELS[item.charge_effect] || { label: item.charge_effect, description: '' };
                    const payoutInfo = PAYOUT_EFFECT_LABELS[item.payout_effect] || { label: item.payout_effect, description: '' };
                    const basisInfo = CALCULATION_BASIS_LABELS[item.calculation_basis] || { label: item.calculation_basis, description: '' };

                    return (
                      <tr key={idx} className="border-b border-[#363232] last:border-b-0">
                        <td className="py-3 px-2 text-[#EBFFEE]">{item.line_item_heading}</td>
                        <td className="py-3 px-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                            item.charge_effect === 'add'
                              ? 'bg-amber-900/30 text-amber-300'
                              : 'bg-blue-900/30 text-blue-300'
                          }`}>
                            {chargeInfo.label}
                          </span>
                          <span className="block text-[#A9A3A3] text-xs mt-1">{chargeInfo.description}</span>
                        </td>
                        <td className="py-3 px-2">
                          <span className="text-[#EBFFEE]">{basisInfo.label}</span>
                          <span className="block text-[#A9A3A3] text-xs">{basisInfo.description}</span>
                        </td>
                        <td className="py-3 px-2 text-[#EBFFEE] font-mono">{feeCalc}</td>
                        <td className="py-3 px-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                            item.payout_effect === 'credit'
                              ? 'bg-green-900/30 text-green-300'
                              : 'bg-red-900/30 text-red-300'
                          }`}>
                            {payoutInfo.label}
                          </span>
                          <span className="block text-[#A9A3A3] text-xs mt-1">{payoutInfo.description}</span>
                        </td>
                        <td className="py-3 px-2">
                          <span className="text-[#EBFFEE]">{recipientInfo.label}</span>
                          <span className="block text-[#A9A3A3] text-xs">{recipientInfo.description}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fee Preview Calculator and Diagram */}
      {schedule.items.length > 0 && (
        <div className="flex flex-wrap gap-6 items-start">
          {/* Fee Preview Calculator */}
          <Card className="bg-[#221F1F] border-[#504A4A] flex-1 min-w-[280px] max-w-xl">
            <CardHeader>
              <CardTitle className="text-[#EBFFEE] text-base">Fee Preview Calculator</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-xs text-[#A9A3A3] mb-1">
                  Subscription amount ($)
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={previewAmount / 100}
                  onChange={(e) => setPreviewAmount(Math.round(parseFloat(e.target.value || '0') * 100))}
                  className="w-32 px-3 py-1.5 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                />
              </div>
              {preview && (
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                    <span className="text-[#A9A3A3] text-sm">Base subscription</span>
                    <span className="text-[#EBFFEE] text-sm">{formatCents(previewAmount)}</span>
                  </div>
                  {preview.lineItems
                    .filter(li => li.chargeEffect === 'add' && li.amountCents > 0)
                    .map((li, idx) => (
                    <div key={idx} className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                      <span className="text-[#A9A3A3] text-sm">{li.heading}</span>
                      <span className="text-[#EBFFEE] text-sm">+{formatCents(li.amountCents)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center px-3 py-2 bg-[#504A4A] rounded-md font-semibold">
                    <span className="text-[#EBFFEE] text-sm">Student pays</span>
                    <span className="text-[#EBFFEE] text-sm">{formatCents(preview.payerCents)}</span>
                  </div>
                  <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                    <span className="text-[#A9A3A3] text-sm">Business receives</span>
                    <span className="text-green-400 text-sm">{formatCents(preview.gymReceives)}</span>
                  </div>
                  <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                    <span className="text-[#A9A3A3] text-sm">Platform fee</span>
                    <span className="text-[#EBFFEE] text-sm">{formatCents(preview.platformFee)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* How Fee Schedules Work Diagram */}
          <div className="flex flex-col items-center py-4">
            {/* Top row: Billable → Pricing Engine → Invoice */}
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 border border-[#504A4A] rounded-lg px-4 py-3 text-center bg-[#221F1F]">
                <div className="text-[#EBFFEE] text-sm font-semibold mb-1">Billable</div>
                <div className="text-[#A9A3A3] text-xs">($100 subscription)</div>
              </div>

              <div className="flex-shrink-0 text-[#504A4A] text-lg">&rarr;</div>

              <div className="flex-shrink-0 border border-[#9B1421] rounded-lg px-4 py-3 text-center bg-[#221F1F]">
                <div className="text-[#EBFFEE] text-sm font-semibold">Pricing Engine</div>
                <div className="text-[#A9A3A3] text-xs">Calculates totals</div>
              </div>

              <div className="flex-shrink-0 text-[#504A4A] text-lg">&rarr;</div>

              <div className="flex-shrink-0 border border-[#504A4A] rounded-lg px-4 py-3 text-center bg-[#221F1F]">
                <div className="text-[#EBFFEE] text-sm font-semibold mb-1">Invoice</div>
                <div className="text-[#A9A3A3] text-xs text-left space-y-0.5">
                  <div>• Customer pays</div>
                  <div>• Business receives</div>
                  <div>• Fees applied</div>
                </div>
              </div>
            </div>

            <div className="text-[#504A4A] text-lg my-2">&uarr;</div>

            <div className="flex-shrink-0 border border-[#504A4A] rounded-lg px-4 py-3 text-center bg-[#221F1F]">
              <div className="text-[#EBFFEE] text-sm font-semibold mb-1">Fee Schedule</div>
              <div className="text-[#A9A3A3] text-xs text-left space-y-0.5">
                <div>• Platform fees</div>
                <div>• Processing fees</div>
                <div>• Adjustments</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
